import { createContext, useContext, useState, type ReactNode } from 'react';
import type { EventsResponse, Evento, RenderResponse } from '@/lib/api';

/** Selección activa: la prueba que se está editando + su contexto. */
export interface Seleccion {
  evento: Evento;
  competicion: string;
  fecha: string;
}

/** Último render generado, para que Resultado sobreviva a la navegación. */
export interface UltimoRender extends RenderResponse {
  kind: 'video' | 'imagen';
  /** Nombre de descarga ya construido (competición + prueba). */
  filename: string;
}

interface SessionState {
  // Home: URL pegada + competición cargada (se conservan al navegar).
  url: string;
  setUrl: (u: string) => void;
  data: EventsResponse | null;
  setData: (d: EventsResponse | null) => void;
  /** URL realmente cargada (no la del input, que el usuario puede editar).
   *  La usa el auto-refresh y se conserva al navegar para poder reanudarlo. */
  loadedUrl: string | null;
  setLoadedUrl: (u: string | null) => void;

  // Editor: prueba seleccionada + foto cargada.
  seleccion: Seleccion | null;
  setSeleccion: (s: Seleccion | null) => void;
  fotoDataUrl: string | undefined;
  setFotoDataUrl: (f: string | undefined) => void;

  // Resultado: último render.
  ultimoRender: UltimoRender | null;
  setUltimoRender: (r: UltimoRender | null) => void;
}

const Ctx = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [url, setUrl] = useState('');
  const [data, setData] = useState<EventsResponse | null>(null);
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useState<Seleccion | null>(null);
  const [fotoDataUrl, setFotoDataUrl] = useState<string | undefined>(undefined);
  const [ultimoRender, setUltimoRender] = useState<UltimoRender | null>(null);

  return (
    <Ctx.Provider
      value={{
        url,
        setUrl,
        data,
        setData,
        loadedUrl,
        setLoadedUrl,
        seleccion,
        setSeleccion,
        fotoDataUrl,
        setFotoDataUrl,
        ultimoRender,
        setUltimoRender,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useSession(): SessionState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSession must be used within <SessionProvider>');
  return v;
}
